// WHAT: Header component for MiracleFlow sessions displaying session type, status, duration, and progress.
// WHY:  Provides consistent header interface for session pages with navigation, status indicators, and progress tracking.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @/components/ui, lucide-react
// RISKS:
//   - NOTE: Session duration and progress are optional; ensure proper handling when undefined.
//   - WARN: Back navigation always calls onBackToDashboard; ensure parent handles active session confirmation.
//   - NOTE: Type-specific styling (colors, gradients) is hardcoded; changes require code updates.

import { ArrowLeft, Sun, Moon, CheckCircle, Clock, Target } from "lucide-react";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";

interface ModernSessionHeaderProperties {
  readonly onBackToDashboard: () => void;
  readonly progress?: number | undefined;
  readonly sessionActive: boolean;
  readonly sessionDuration?: string | undefined;
  readonly sessionType: "Evening" | "Morning";
}

/**
 * WHAT:
 *   Header component for MiracleFlow sessions.
 *
 * WHY:
 *   Provides consistent header interface for session pages with navigation back to dashboard,
 *   session type indicators, active status badge, duration display, and progress tracking.
 *
 * PROPS:
 *   - sessionType: 'Morning' | 'Evening' - Type of session
 *   - sessionActive: boolean - Flag indicating if session is currently active
 *   - onBackToDashboard: () => void - Callback for back navigation
 *   - sessionDuration?: string - Optional formatted duration string (e.g., "MM:SS")
 *   - progress?: number - Optional progress percentage (0-100)
 *
 * BEHAVIOR:
 *   - Displays back button to navigate to dashboard
 *   - Shows session type icon, title, and subtitle with type-specific styling
 *   - Displays session duration if provided
 *   - Shows progress percentage if provided
 *   - Renders active session badge when sessionActive is true
 *   - Applies type-specific gradient colors and borders
 *
 * RISKS:
 *   - NOTE: Session duration and progress are optional; ensure proper handling when undefined.
 *   - WARN: Back navigation always calls onBackToDashboard; ensure parent handles active session confirmation.
 *   - NOTE: Type-specific styling (colors, gradients) is hardcoded; changes require code updates.
 *
 * @param root0
 *
 * @param root0.sessionType
 *
 * @param root0.sessionActive
 *
 * @param root0.onBackToDashboard
 *
 * @param root0.sessionDuration
 *
 * @param root0.progress
 *
 * @returns JSX.Element
 *
 */
export function ModernSessionHeader({
  onBackToDashboard,
  progress,
  sessionActive,
  sessionDuration,
  sessionType,
}: ModernSessionHeaderProperties) {
  const isMorning = sessionType === "Morning";
  const Icon = isMorning ? Sun : Moon;

  const config = {
    Evening: {
      borderColor: "border-tracq-light-orange/30",
      gradient: "from-tracq-deep-orange/20 to-tracq-light-orange/20",
      iconGradient: "from-tracq-deep-orange to-tracq-light-orange",
      subtitle: "Reflect and prepare for tomorrow",
      title: "Evening Session",
    },
    Morning: {
      borderColor: "border-tracq-bright-blue/30",
      gradient: "from-tracq-deep-blue/20 to-tracq-bright-blue/20",
      iconGradient: "from-tracq-deep-blue to-tracq-bright-blue",
      subtitle: "Start your day with intention and purpose",
      title: "Morning Session",
    },
  };

  const currentConfig = config[sessionType];

  return (
    <Card
      className={`bg-gradient-to-br from-[#1E293B] to-[#0F172A] ${currentConfig.borderColor} border-2`}
      padding="lg"
      variant="elevated"
    >
      <Stack align="center" direction="row" justify="between">
        {/* Left Section - Back Button & Title */}
        <Stack align="center" direction="row" gap={6}>
          <Button
            className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50 hover:text-tracq-bright-blue transition-all duration-200"
            onClick={onBackToDashboard}
            size="sm"
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Stack align="center" direction="row" gap={4}>
            <div
              className={`flex items-center justify-center w-14 h-14 rounded-tracq bg-gradient-to-br ${currentConfig.iconGradient} shadow-tracq-card`}
            >
              <Icon className="w-7 h-7 text-white" />
            </div>
            <Stack direction="col" gap={1}>
              <Heading className="text-[#F1F5F9] font-bold" level="h1" size="xl">
                {currentConfig.title}
              </Heading>
              <p className="text-[#94A3B8] text-lg">{currentConfig.subtitle}</p>
            </Stack>
          </Stack>
        </Stack>

        {/* Right Section - Status & Info */}
        <Stack align="center" direction="row" gap={4}>
          {/* Session Duration */}
          {sessionDuration ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#334155] rounded-tracq border border-[#475569]">
              <Clock className="w-4 h-4 text-tracq-bright-blue" />
              <span className="text-sm font-medium text-[#F1F5F9]">{sessionDuration}</span>
            </div>
          ) : null}

          {/* Progress Badge */}
          {progress !== undefined && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#334155] rounded-tracq border border-[#475569]">
              <Target className="w-4 h-4 text-tracq-bright-blue" />
              <span className="text-sm font-medium text-[#F1F5F9]">{Math.round(progress)}%</span>
            </div>
          )}

          {/* Active Status */}
          {sessionActive ? (
            <Badge
              className="bg-green-500/20 text-green-400 border-green-500/30"
              size="lg"
              variant="success"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Active Session
            </Badge>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
}
